import React from 'react'
import { Button, Space, Tag, message, Popconfirm, Table, Card } from 'antd'
import { DeleteOutlined, UnlockOutlined, ReloadOutlined } from '@ant-design/icons'
import API from '../../../../utils/api'
import { useToken } from '../../../hooks'
import EntryTime from '../../../components/PositionColumns/EntryTime'
import useSWR from 'swr'

const UserOTPSetting = () => {
	const { data: token } = useToken()

	const apiKey = `/admin/user-otp?t=`
	const { data, mutate } = useSWR<any>(
		token ? apiKey : null,
		async () => {
			const response = await API.get(apiKey, token + '', {})
			return response?.result?.result || response?.result || {}
		}
	)

	const list = data?.list || []

	const handleReset = async (userId: number) => {
		await API.send('DELETE', '/admin/user-otp', token + '', { userId })
		message.success('OTP가 리셋되었습니다. 파트너에게 접속기에서 재설정하도록 안내하세요.')
		mutate()
	}

	const handleUnlock = async (userId: number) => {
		await API.send('PUT', '/admin/user-otp/unlock', token + '', { userId })
		message.success('잠금이 해제되었습니다.')
		mutate()
	}

	const getStatusTag = (record: any) => {
		if (!record.otp_enabled) {
			return <Tag color="default">미활성</Tag>
		}
		if (record.locked_until && new Date(record.locked_until) > new Date()) {
			return <Tag color="red">잠금</Tag>
		}
		return <Tag color="green">활성</Tag>
	}

	const columns = [
		{
			title: '유저ID',
			dataIndex: 'user_id',
			key: 'user_id',
			width: 80,
			align: 'center' as const,
		},
		{
			title: '레벨',
			dataIndex: 'level',
			key: 'level',
			width: 60,
			align: 'center' as const,
			render: (text: number) => (
				<Tag color={text === 5 ? 'purple' : text >= 9 ? 'gold' : 'blue'}>
					{text}
				</Tag>
			),
		},
		{
			title: '이름',
			key: 'name',
			width: 140,
			render: (_: any, record: any) => (
				<span style={{ fontSize: 12 }}>
					{record.fullname || record.nickname || record.loginid || '-'}
				</span>
			),
		},
		{
			title: 'OTP 상태',
			key: 'status',
			width: 80,
			align: 'center' as const,
			render: (_: any, record: any) => getStatusTag(record),
		},
		{
			title: '마지막 인증',
			dataIndex: 'last_verified_at',
			key: 'last_verified_at',
			width: 160,
			align: 'center' as const,
			render: (text: string) =>
				text ? <EntryTime time={text} /> : <span style={{ color: '#999' }}>-</span>,
		},
		{
			title: '실패횟수',
			dataIndex: 'failed_attempts',
			key: 'failed_attempts',
			width: 80,
			align: 'center' as const,
			render: (text: number) => (
				<span style={{ color: text >= 5 ? '#f5222d' : undefined }}>
					{text || 0}
				</span>
			),
		},
		{
			title: '등록일',
			dataIndex: 'created_at',
			key: 'created_at',
			width: 160,
			align: 'center' as const,
			render: (text: string) => <EntryTime time={text} />,
		},
		{
			title: '관리',
			key: 'action',
			width: 150,
			align: 'center' as const,
			render: (_: any, record: any) => (
				<Space>
					<Popconfirm
						title="OTP를 리셋하시겠습니까? 파트너가 접속기에서 재설정해야 합니다."
						onConfirm={() => handleReset(record.user_id)}>
						<Button size="small" danger icon={<DeleteOutlined />}>
							리셋
						</Button>
					</Popconfirm>
					{record.locked_until &&
						new Date(record.locked_until) > new Date() && (
							<Popconfirm
								title="잠금을 해제하시겠습니까?"
								onConfirm={() => handleUnlock(record.user_id)}>
								<Button size="small" icon={<UnlockOutlined />}>
									잠금해제
								</Button>
							</Popconfirm>
						)}
				</Space>
			),
		},
	]

	return (
		<div style={{ padding: '16px 0' }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 16,
				}}>
				<h3 style={{ margin: 0 }}>
					OTP 사용자 관리 <Tag>{list.length}명</Tag>
				</h3>
				<Button icon={<ReloadOutlined />} onClick={() => mutate()}>
					새로고침
				</Button>
			</div>

			{list.length === 0 ? (
				<Card>
					<span style={{ color: '#999' }}>
						OTP가 설정된 사용자가 없습니다.
					</span>
				</Card>
			) : (
				<Table
					dataSource={list}
					columns={columns}
					rowKey="id"
					size="small"
					pagination={false}
				/>
			)}
		</div>
	)
}

export default React.memo(UserOTPSetting)
